import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import type { DocumentItem, DocumentUploadResult } from '../types';

interface DocumentState {
  key: string;
  documents: DocumentItem[];
  status: 'loading' | 'ready' | 'error';
  error: string;
}

export function useTicketDocuments(id: string, runId: string, enabled = true) {
  const key = JSON.stringify([id, runId]);
  const [state, setState] = useState<DocumentState>({ key, documents: [], status: 'loading', error: '' });
  const currentKey = useRef(key);
  currentKey.current = key;
  const request = useRef(0);
  const controller = useRef<AbortController | undefined>(undefined);

  const reload = useCallback(async () => {
    if (!enabled) return;
    controller.current?.abort();
    const pending = new AbortController();
    controller.current = pending;
    const version = ++request.current;
    setState((previous) => ({ key, documents: previous.key === key ? previous.documents : [], status: 'loading', error: '' }));
    try {
      const documents = await api.documents(id, runId, pending.signal);
      if (version !== request.current || pending.signal.aborted || currentKey.current !== key) return;
      setState({ key, documents, status: 'ready', error: '' });
    } catch (reason) {
      if (version !== request.current || pending.signal.aborted || currentKey.current !== key) return;
      const message = reason instanceof Error ? reason.message : 'Reîncercați citirea listei.';
      setState((previous) => ({ ...previous, status: 'error', error: `Documentele și fotografiile nu pot fi încărcate: ${message}` }));
    }
  }, [id, runId, key, enabled]);

  useEffect(() => {
    void reload();
    return () => { request.current++; controller.current?.abort(); };
  }, [reload]);

  const acceptUpload = useCallback((result: DocumentUploadResult) => {
    if (currentKey.current !== key) return;
    request.current++;
    controller.current?.abort();
    if (Array.isArray(result.documents)) {
      setState({ key, documents: result.documents, status: 'ready', error: '' });
    } else {
      setState((previous) => ({
        key, documents: previous.key === key ? previous.documents : [], status: 'error',
        error: result.documentsError || 'Uploadul a fost acceptat, dar lista fotografiilor nu a putut fi verificată. Reîncercați citirea listei.',
      }));
    }
  }, [key]);

  return {
    documents: state.key === key ? state.documents : [],
    documentsReady: enabled && state.key === key && state.status === 'ready',
    documentsLoading: enabled && (state.key !== key || state.status === 'loading'),
    documentsError: state.key === key ? state.error : '',
    reloadDocuments: reload,
    acceptUpload,
  };
}
